import { useState, useEffect, useContext, useRef, Fragment } from "react";
import ReactDOM from "react-dom";
import { post, C } from "./utils";
import { ThemeCtx } from "./AppShell";

const API = "https://cip.medsphere.net/api";

async function apiFetch(endpoint, body, user) {
  return post(endpoint, { userid: user.USERID, ...body }, user.AUTH_CODE);
}
async function apiPost(endpoint, fd, user) {
  const r = await fetch(`${API}/${endpoint}`, {
    method: "POST", headers: { "Auth-Code": user.AUTH_CODE }, body: fd,
  });
  return r.json();
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PERMISSIONS = [
  { key:"capture",          label:"Capture"          },
  { key:"gallery",          label:"Gallery"          },
  { key:"events",           label:"Events"           },
  { key:"news",             label:"News"             },
  { key:"kits",             label:"KITs/KIQs"        },
  { key:"reports",          label:"Reports"          },
  { key:"congress",         label:"Congress"         },
  { key:"chat",             label:"Chat"             },
  { key:"vchat",            label:"View Chat"        },
  { key:"export",           label:"Export"           },
  { key:"dm",               label:"Direct Message"   },
  { key:"congress_admin",   label:"Congress Admin"   },
  { key:"clinical_trials",  label:"Clinical Trials"  },
  { key:"product",          label:"Product"          },
  { key:"timelines",        label:"Timelines"        },
  { key:"newsletter",       label:"NewsFlash"        },
  { key:"workshop",         label:"Workshop"         },
  { key:"at_a_glance",      label:"At a Glance"      },
  { key:"research_request", label:"Research Request" },
  { key:"medadvisory",      label:"MedAdvisory"      },
  { key:"echo",             label:"Echo"             },
  { key:"navigator",        label:"Navigator"        },
  // visible and navigator_admin rendered manually after this list
  // navigator_admin rendered conditionally below when navigator is enabled
];

const PERM_API_MAP = {
  CAPTURE:"capture", GALLERY:"gallery", UPCOMING_EVENTS:"events",
  NEWS:"news", KITS:"kits", REPORTS:"reports", CONGRESS:"congress",
  CHAT:"chat", VIEWCHAT:"vchat", EXPORT:"export", "DIRECT MESSAGE":"dm",
  CONGRESS_ADMIN:"congress_admin", VISIBLE:"visible",
  CLINICAL_TRIALS:"clinical_trials", PRODUCT:"product",
  TIMELINES:"timelines", NEWSLETTER:"newsletter", WORKSHOP:"workshop",
  AT_A_GLANCE:"at_a_glance", RESEARCH_REQUEST:"research_request",
  MEDADVISORY:"medadvisory", ECHO:"echo", NAVIGATOR:"navigator",
  NAVIGATOR_ADMIN:"navigator_admin",
};

const ROLES = [
  { v:0,   l:"User"            },
  { v:1,   l:"Editor"          },
  { v:3,   l:"Power User"      },
  { v:4,   l:"Content Manager" },
  { v:5,   l:"Super User"      },
  { v:101, l:"Deactivated"     },
];

const SECURITY_LEVELS = [
  { v:0, l:"Open"   },
  { v:1, l:"Low"    },
  { v:2, l:"Medium" },
  { v:3, l:"High"   },
];

const DEFAULT_TABS = [
  { v:"0",  l:"Dashboard"       },
  { v:"2",  l:"Gallery"         },
  { v:"1",  l:"Capture"         },
  { v:"3",  l:"Upcoming Events" },
  { v:"4",  l:"News"            },
  { v:"5",  l:"Reports"         },
  { v:"6",  l:"Congress"        },
  { v:"7",  l:"KITs & KIQs"     },
  { v:"8",  l:"Clinical Trials" },
  { v:"9",  l:"Product"         },
  { v:"10", l:"Timelines"       },
  { v:"11", l:"NewsFlash"       },
  { v:"12", l:"Workshop"        },
  { v:"13", l:"At a Glance"     },
];

// ─── Date helpers ─────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function parseExpiry(expStr) {
  if(!expStr) return null;
  const parts = expStr.split("-");
  if(parts.length!==3) return null;
  const year = parts[2].length===2 ? "20"+parts[2] : parts[2];
  return new Date(`${parts[1]} ${parts[0]} ${year}`);
}

function deriveStatus(u) {
  const exp = parseExpiry(u.EXPIRYDATE);
  const expired = exp && exp < new Date();
  if(expired) return "Expired";
  if(u.USERSTATUS==="C") return "Active";
  return "Inactive";
}

function normalizeExpiry(raw) {
  if(!raw) return "";
  if(/^\d{2}-[A-Za-z]{3}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if(isNaN(d)) return raw;
  const day = String(d.getUTCDate()).padStart(2,"0");
  const mon = MONTHS[d.getUTCMonth()];
  const yr  = String(d.getUTCFullYear()).slice(2);
  return `${day}-${mon}-${yr}`;
}

function expiryToInput(expStr) {
  if(!expStr) return "";
  const parts = expStr.split("-");
  if(parts.length!==3) return "";
  const day   = parts[0].padStart(2,"0");
  const mIdx  = MONTHS.indexOf(parts[1]);
  if(mIdx===-1) return "";
  const month = (mIdx+1).toString().padStart(2,"0");
  const year  = parts[2].length===2 ? "20"+parts[2] : parts[2];
  return `${year}-${month}-${day}`;
}

function inputToExpiry(dateStr) {
  if(!dateStr) return "";
  const [y,m,d] = dateStr.split("-");
  if(!y||!m||!d) return "";
  const mon = MONTHS[Number(m)-1];
  if(!mon) return "";
  return `${d}-${mon}-${y.slice(2)}`;
}

// ─── Shared input style ───────────────────────────────────────────────────────
const inp = {
  width:"100%", boxSizing:"border-box", padding:"7px 10px",
  borderRadius:7, border:`1px solid ${C.border}`, fontSize:13,
  color:C.text, background:"#fff", outline:"none", fontFamily:"inherit",
};

// ─── StatusBadge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = status||"";
  const cfg = s==="Active"
    ? { bg:"#E1F5EE", color:"#1D9E75" }
    : s==="Expired"
    ? { bg:"#FEF3C7", color:"#D97706" }
    : { bg:"#FEE2E2", color:"#DC2626" };
  return (
    <span style={{fontSize:10,fontWeight:700,borderRadius:5,
      padding:"1px 7px",background:cfg.bg,color:cfg.color}}>
      {s||"Inactive"}
    </span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if(!toast) return null;
  return (
    <div style={{position:"fixed",bottom:24,right:24,zIndex:3000,
      padding:"11px 16px",borderRadius:10,maxWidth:320,
      background:toast.ok?"#E1F5EE":"#FEF2F2",
      border:`1px solid ${toast.ok?"#6FCFA7":"#FECACA"}`,
      color:toast.ok?"#1D9E75":"#B91C1C",
      boxShadow:"0 8px 24px rgba(0,0,0,0.12)",fontSize:13,fontWeight:500,
      display:"flex",alignItems:"center",gap:8,
      animation:"umToast 0.2s ease"}}>
      {toast.ok?"✓":"⚠️"} {toast.msg}
    </div>
  );
}

// ─── ChipRow — single-select, wraps to multiple rows ─────────────────────────
function ChipRow({ options, value, onChange, idKey, labelKey, error, primary }) {
  return (
    <div style={{display:"flex",gap:5,flexWrap:"wrap",padding:"4px 0"}}>
      {options.map(opt=>{
        const id=opt[idKey]; const sel=String(value)===String(id);
        return(
          <button key={id} onClick={()=>onChange(id)}
            style={{flexShrink:0,padding:"3px 11px",borderRadius:20,cursor:"pointer",
              fontSize:11,fontWeight:sel?700:400,transition:"all .12s",whiteSpace:"nowrap",
              border:`1px solid ${sel?primary:(error?C.error:C.border)}`,
              background:sel?primary:"#fff",color:sel?"#fff":C.muted}}>
            {opt[labelKey]}
          </button>
        );
      })}
    </div>
  );
}

// ─── MultiChipRow — multi-select, wraps to multiple rows ─────────────────────
function MultiChipRow({ options, selected, onToggle, idKey, labelKey, disabledFn, primary, accentLight }) {
  return (
    <div style={{display:"flex",gap:5,flexWrap:"wrap",padding:"4px 0"}}>
      {options.map(opt=>{
        const id=opt[idKey]; const sel=selected.includes(id);
        const disabled=disabledFn?disabledFn(opt):false;
        return(
          <button key={id} disabled={disabled}
            onClick={()=>{ if(disabled) return; onToggle(id); }}
            style={{flexShrink:0,padding:"3px 11px",borderRadius:20,
              fontSize:11,fontWeight:sel?700:400,transition:"all .12s",whiteSpace:"nowrap",
              cursor:disabled?"not-allowed":"pointer",opacity:disabled?0.4:1,
              border:`1px solid ${sel?primary:C.border}`,
              background:sel?accentLight:"#fff",color:sel?primary:C.muted}}>
            {opt[labelKey]}
          </button>
        );
      })}
    </div>
  );
}

// ─── UserForm (inline right panel) ───────────────────────────────────────────
function UserForm({ user, activeSub, drops, editUser, onClose, onSaved, onToast, primary, accentLight, theme }) {
  const isEdit    = !!editUser;
  const groups     = drops[0]||[];
  const franchises = drops[1]||[];
  const countries  = drops[2]||[];

  const init = () => {
    if(!isEdit) {
      const perms = {};
      PERMISSIONS.forEach(p => perms[p.key] = 0);
      return { name:"", email:"", password:"", mobile:"", expdate:"",
        countryid:"", role:4, securelvl:0, group:"", defaultTab:"13",
        subprojects:[activeSub.SUBPROJECTID], optgroups:[], perms };
    }
    const perms = {};
    Object.entries(PERM_API_MAP).forEach(([k,fk]) => { perms[fk] = Number(editUser[k])||0; });
    const optIds = editUser.OPTIONALGROUPID
      ? editUser.OPTIONALGROUPID.split(",").map(x=>Number(x.trim())).filter(Boolean) : [];
    const franchiseIds = Array.isArray(editUser.FRANCHISE)
      ? editUser.FRANCHISE.map(f=>f.SUBPROJECTID) : [activeSub.SUBPROJECTID];
    return {
      name:       editUser.NAME||"",
      email:      editUser.EMAIL||"",
      password:   editUser.PASSWORD||"",
      mobile:     editUser.MOBILE||"",
      expdate:    normalizeExpiry(editUser.EXPIRYDATE),
      countryid:  editUser.COUNTRYID||"",
      role:       editUser.ROLE!=null ? Number(editUser.ROLE) : 0,
      securelvl:  editUser.SECURELVL||0,
      group:      editUser.USER_GROUPID||"",
      defaultTab: String(editUser.DEFAULT||"0"),
      subprojects: franchiseIds,
      optgroups:  optIds,
      perms,
    };
  };

  const [form,    setForm]    = useState(init);
  const [saving,  setSaving]  = useState(false);
  const [section, setSection] = useState("basic");
  const [showPwd, setShowPwd] = useState(false);
  const [errors,  setErrors]  = useState({});

  const set = (k,v) => { setForm(f=>({...f,[k]:v})); setErrors(e=>({...e,[k]:null})); };
  const togglePerm = (k) => setForm(f=>{
    const newVal = f.perms[k] ? 0 : 1;
    const perms  = { ...f.perms, [k]: newVal };
    // auto-enable view chat when chat is enabled
    if(k==="chat" && newVal===1) perms.vchat = 1;
    return { ...f, perms };
  });
  const toggleAll  = (val) => setForm(f=>{
    const perms={}; PERMISSIONS.forEach(p=>perms[p.key]=val); return {...f,perms};
  });

  const validate = () => {
    const e = {};
    if(!form.name.trim())             e.name        = "Full name is required";
    if(!isEdit&&!form.email.trim())   e.email       = "Email is required";
    if(!form.password.trim())         e.password    = "Password is required";
    if(!form.expdate)                 e.expdate     = "Expiry date is required";
    if(!form.countryid)               e.countryid   = "Country is required";
    if(form.group==="")               e.group       = "Primary group is required";
    if(!form.subprojects.length)      e.subprojects = "Select at least one franchise";
    setErrors(e);
    if(Object.keys(e).length>0){ setSection("basic"); return false; }
    return true;
  };

  const save = async () => {
    if(!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if(isEdit) {
        fd.append("userid", editUser.USERID);
      } else {
        fd.append("userid", user.USERID);
        fd.append("email",  form.email.trim());
      }
      fd.append("name",      form.name.trim());
      fd.append("password",  form.password);
      fd.append("expdate",   form.expdate);
      fd.append("countryid", form.countryid);
      fd.append("role",      form.role);
      fd.append("securelvl", form.securelvl);
      fd.append("group",     form.group);
      fd.append("default",   form.defaultTab);
      if(form.mobile) fd.append("mobile", form.mobile);
      form.subprojects.forEach(id => fd.append("subprojectid[]", id));
      form.optgroups.forEach(id   => fd.append("optgroup[]",     id));
      PERMISSIONS.forEach(p       => fd.append(p.key, form.perms[p.key]||0));
      fd.append("navigator_admin", form.perms.navigator_admin||0);
      fd.append("visible",         form.perms.visible||0);
      const res = await apiPost(isEdit?"edit_user":"add_user", fd, user);
      if(res.STATUS==="1"||res.STATUS===1) {
        onToast({ok:true, msg:isEdit?"User updated!":"User added successfully!"});
        setTimeout(()=>onSaved(), 600);
      } else {
        onToast({ok:false, msg:res.MESSAGE||res.message||"Something went wrong."});
      }
    } catch { onToast({ok:false, msg:"Network error."}); }
    setSaving(false);
  };

  const Label = ({children, required}) => (
    <label style={{display:"block",fontSize:11,fontWeight:700,color:C.hint,
      textTransform:"uppercase",letterSpacing:"0.4px",marginBottom:5}}>
      {children}{required&&<span style={{color:"#E53E3E",marginLeft:3}}>*</span>}
    </label>
  );
  const ErrMsg = ({field}) => errors[field]
    ? <div style={{fontSize:11,color:"#E53E3E",marginTop:3}}>{errors[field]}</div> : null;
  const fStyle = (field) => ({...inp,
    border:`1px solid ${errors[field]?"#E53E3E":C.border}`,
    background:errors[field]?"#FFF5F5":"#fff"});

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>

      {/* header */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${C.border}`,
        display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <button onClick={onClose}
          style={{width:28,height:28,borderRadius:7,border:`1px solid ${C.border}`,
            background:"#F7F9FC",color:C.muted,cursor:"pointer",fontSize:14,
            display:"flex",alignItems:"center",justifyContent:"center"}}>←</button>
        <div style={{fontSize:15,fontWeight:700,color:C.text}}>
          {isEdit?`Edit — ${editUser.NAME}`:"Add User"}
        </div>
        <div style={{flex:1}}/>
        <button onClick={save} disabled={saving}
          style={{padding:"7px 22px",borderRadius:7,border:"none",
            background:saving?"#aaa":primary,color:"#fff",
            fontSize:13,fontWeight:600,cursor:saving?"wait":"pointer",
            boxShadow:`0 2px 8px ${primary}44`}}>
          {saving?"Saving…":isEdit?"Save Changes":"Add User"}
        </button>
      </div>

      {/* tabs */}
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,
        flexShrink:0,alignItems:"center"}}>
        {[{id:"basic",label:"👤 Details"},{id:"perms",label:"🔐 Permissions"}].map(t=>(
          <button key={t.id} onClick={()=>setSection(t.id)}
            style={{padding:"9px 20px",border:"none",cursor:"pointer",background:"transparent",
              fontSize:13,fontWeight:section===t.id?700:500,
              color:section===t.id?primary:C.muted,
              borderBottom:`2px solid ${section===t.id?primary:"transparent"}`,
              transition:"all .12s"}}>
            {t.label}
          </button>
        ))}
        {Object.keys(errors).length>0&&(
          <span style={{marginLeft:8,fontSize:11,color:"#E53E3E",display:"flex",alignItems:"center",gap:4}}>
            ⚠️ Required: {Object.values(errors).filter(Boolean).map(e=>e.replace(" is required","").replace("Select at least one ","")).join(", ")}
          </span>
        )}
      </div>

      {/* body */}
      <div style={{flex:1,overflowY:"auto",padding:"20px"}}>

        {section==="basic"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px 20px",maxWidth:720}}>

            <div style={{gridColumn:"1/-1"}}>
              <Label required>Full Name</Label>
              <input value={form.name} onChange={e=>set("name",e.target.value)}
                placeholder="John Smith" style={fStyle("name")}/>
              <ErrMsg field="name"/>
            </div>

            {!isEdit&&(
              <div style={{gridColumn:"1/-1"}}>
                <Label required>Email</Label>
                <input value={form.email} onChange={e=>set("email",e.target.value)}
                  placeholder="user@company.com" style={fStyle("email")}/>
                <ErrMsg field="email"/>
              </div>
            )}

            <div>
              <Label required>Password</Label>
              <div style={{position:"relative"}}>
                <input type={showPwd?"text":"password"} value={form.password}
                  onChange={e=>set("password",e.target.value)}
                  placeholder="••••••••" style={{...fStyle("password"),paddingRight:36}}/>
                <button type="button" onClick={()=>setShowPwd(p=>!p)}
                  style={{position:"absolute",right:9,top:"50%",transform:"translateY(-50%)",
                    background:"none",border:"none",cursor:"pointer",color:C.hint,padding:2}}>
                  {showPwd
                    ?<svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    :<svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                  }
                </button>
              </div>
              <ErrMsg field="password"/>
            </div>

            <div>
              <Label>Mobile</Label>
              <input value={form.mobile} onChange={e=>set("mobile",e.target.value)}
                placeholder="+1 555 867 5309" style={inp}/>
            </div>

            <div>
              <Label required>Expiry Date</Label>
              <input type="date" value={expiryToInput(form.expdate)}
                onChange={e=>set("expdate",inputToExpiry(e.target.value))}
                style={fStyle("expdate")}/>
              {form.expdate&&(
                <div style={{fontSize:10,color:C.hint,marginTop:3}}>
                  Sends as: <strong>{form.expdate}</strong>
                </div>
              )}
              <ErrMsg field="expdate"/>
            </div>

            <div>
              <Label required>Country</Label>
              <ChipRow primary={primary}
                options={countries} value={form.countryid}
                onChange={v=>{set("countryid",v)}}
                idKey="COUNTRYID" labelKey="COUNTRYNAME"
                error={!!errors.countryid}/>
              <ErrMsg field="countryid"/>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label required>Role</Label>
              <ChipRow primary={primary}
                options={ROLES.filter(r=>r.v!==101)} value={form.role}
                onChange={v=>set("role",Number(v))}
                idKey="v" labelKey="l"/>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label required>Security Level</Label>
              <ChipRow primary={primary}
                options={SECURITY_LEVELS} value={form.securelvl}
                onChange={v=>set("securelvl",Number(v))}
                idKey="v" labelKey="l"/>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label required>Primary Group</Label>
              <ChipRow primary={primary}
                options={groups} value={form.group}
                onChange={v=>set("group",v)}
                idKey="USER_GROUPID" labelKey="USER_GROUPNAME"
                error={!!errors.group}/>
              <ErrMsg field="group"/>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label>Default Tab</Label>
              <ChipRow primary={primary}
                options={DEFAULT_TABS} value={form.defaultTab}
                onChange={v=>set("defaultTab",v)}
                idKey="v" labelKey="l"/>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label required>Franchises / Subprojects</Label>
              <div style={{border:`1px solid ${errors.subprojects?"#E53E3E":C.border}`,borderRadius:8}}>
                <MultiChipRow primary={primary} accentLight={accentLight}
                  options={franchises} selected={form.subprojects}
                  onToggle={id=>{setErrors(e=>({...e,subprojects:null}));setForm(prev=>({...prev,
                    subprojects:prev.subprojects.includes(id)
                      ?prev.subprojects.filter(x=>x!==id):[...prev.subprojects,id]}));}}
                  idKey="SUBPROJECTID" labelKey="SUMMARY"/>
              </div>
              <ErrMsg field="subprojects"/>
              <div style={{fontSize:11,color:C.hint,marginTop:3}}>
                Removing a franchise will auto-deactivate access for that subproject.
              </div>
            </div>

            <div style={{gridColumn:"1/-1"}}>
              <Label>Optional Groups</Label>
              <MultiChipRow primary={primary} accentLight={accentLight}
                options={groups.map(g=>({
                  ...g,
                  _disabled: String(g.USER_GROUPID)===String(form.group)
                }))}
                selected={form.optgroups}
                onToggle={id=>setForm(f=>({...f,
                  optgroups: f.optgroups.includes(id)
                    ? f.optgroups.filter(x=>x!==id)
                    : [...f.optgroups, id]}))}
                idKey="USER_GROUPID" labelKey="USER_GROUPNAME"
                disabledFn={g=>String(g.USER_GROUPID)===String(form.group)}/>
            </div>

          </div>
        )}

        {section==="perms"&&(
          <div style={{maxWidth:760}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:12,fontWeight:600,color:C.text}}>Feature Permissions</span>
              <div style={{display:"flex",gap:5}}>
                <button onClick={()=>toggleAll(1)}
                  style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${C.border}`,
                    background:"#F7F9FC",color:C.muted,fontSize:11,cursor:"pointer"}}>Enable All</button>
                <button onClick={()=>toggleAll(0)}
                  style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${C.border}`,
                    background:"#F7F9FC",color:C.muted,fontSize:11,cursor:"pointer"}}>Disable All</button>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:"6px"}}>
              {PERMISSIONS.map(p=>{
                const on=!!form.perms[p.key];
                return(
                  <Fragment key={p.key}>
                    <button onClick={()=>togglePerm(p.key)}
                      style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",
                        borderRadius:7,cursor:"pointer",textAlign:"left",
                        border:`1px solid ${on?primary:C.border}`,
                        background:on?accentLight:"#F7F9FC",transition:"all .12s"}}>
                      <div style={{width:13,height:13,borderRadius:3,flexShrink:0,
                        border:`2px solid ${on?primary:"#D1D5DB"}`,
                        background:on?primary:"#fff",
                        display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {on&&<svg width="7" height="7" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>}
                      </div>
                      <span style={{fontSize:11,fontWeight:on?600:400,color:on?primary:C.text}}>{p.label}</span>
                    </button>
                    {/* navigator_admin — inline right after navigator */}
                    {p.key==="navigator" && !!form.perms.navigator && (()=>{
                      const na=!!form.perms.navigator_admin;
                      return(
                        <button onClick={()=>togglePerm("navigator_admin")}
                          style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",
                            borderRadius:7,cursor:"pointer",textAlign:"left",
                            border:`1px solid ${na?primary:"#F59E0B"}`,
                            background:na?accentLight:"#FFFBEB",transition:"all .12s"}}>
                          <div style={{width:13,height:13,borderRadius:3,flexShrink:0,
                            border:`2px solid ${na?primary:"#F59E0B"}`,
                            background:na?primary:"#fff",
                            display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {na&&<svg width="7" height="7" viewBox="0 0 10 10" fill="none">
                              <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>}
                          </div>
                          <span style={{fontSize:11,fontWeight:na?600:400,color:na?primary:"#92400E"}}>
                            Nav Admin
                          </span>
                        </button>
                      );
                    })()}
                  </Fragment>
                );
              })}
            </div>

            {/* navigator_admin — only visible when navigator is enabled */}
            {/* Visible — bottom right toggle */}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:12}}>
              <button onClick={()=>togglePerm("visible")}
                style={{display:"flex",alignItems:"center",gap:8,padding:"7px 16px",
                  borderRadius:20,cursor:"pointer",border:"none",
                  background:form.perms.visible?"#0F172A":"#F1F5F9",
                  transition:"all .15s"}}>
                <div style={{width:32,height:18,borderRadius:9,position:"relative",
                  background:form.perms.visible?"#4ADE80":"#CBD5E1",transition:"background .2s"}}>
                  <div style={{position:"absolute",top:2,
                    left:form.perms.visible?14:2,
                    width:14,height:14,borderRadius:"50%",background:"#fff",
                    boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left .2s"}}/>
                </div>
                <span style={{fontSize:12,fontWeight:600,
                  color:form.perms.visible?"#4ADE80":"#94A3B8"}}>
                  {form.perms.visible?"Visible to other users":"Hidden from other users"}
                </span>
              </button>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}

// ─── UserRow ──────────────────────────────────────────────────────────────────
function UserRow({ u, primary, accentLight, onEdit, compact, adminUser, onRefresh }) {
  const [hov,     setHov]     = useState(false);
  const [menuOpen,setMenuOpen] = useState(false);
  const [actLoad, setActLoad]  = useState(false);
  const [actMsg,  setActMsg]   = useState("");
  const menuRef = useRef(null);

  const ini      = (u.NAME||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  const status   = deriveStatus(u);
  const franchises = Array.isArray(u.FRANCHISE)?u.FRANCHISE:[];
  const roleName = ROLES.find(r=>r.v===Number(u.ROLE))?.l||`Role ${u.ROLE}`;
  const isActive = status==="Active";
  const isDeleted= Number(u.ROLE)===101;

  useEffect(()=>{
    if(!menuOpen) return;
    const handler=e=>{if(menuRef.current&&!menuRef.current.contains(e.target))setMenuOpen(false);};
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[menuOpen]);

  const doAction = async (action) => {
    setMenuOpen(false); setActLoad(true); setActMsg("");
    try {
      const fd=new URLSearchParams();
      fd.append("adminid", adminUser.USERID);
      fd.append("userid",  u.USERID);
      fd.append("action",  action);
      const r = await fetch(`${API}/user_action`,{
        method:"POST",
        headers:{"Auth-Code":adminUser.AUTH_CODE,"Content-Type":"application/x-www-form-urlencoded"},
        body:fd.toString()
      });
      const res = await r.json();
      setActMsg(res.MESSAGE||"Done");
      if(String(res.STATUS)==="1") onRefresh?.();
    } catch { setActMsg("Network error"); }
    setActLoad(false);
    setTimeout(()=>setActMsg(""),3000);
  };

  return(
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={e=>{if(!menuOpen)onEdit(u);}}
      style={{position:"relative",display:"flex",alignItems:"center",gap:10,
        padding:compact?"9px 12px":"10px 16px",
        borderBottom:`1px solid ${C.border}`,
        background:hov?"#F7F9FC":"#fff",
        transition:"background .1s",cursor:"pointer"}}>

      <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,
        background:`${primary}20`,display:"flex",alignItems:"center",
        justifyContent:"center",fontSize:12,fontWeight:800,color:primary}}>
        {ini}
      </div>

      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:600,color:C.text,
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:150}}>
            {u.NAME}
          </span>
          <StatusBadge status={status}/>
          {!compact&&<span style={{fontSize:10,background:"#F0F0F0",color:C.muted,
            borderRadius:4,padding:"1px 6px",fontWeight:600}}>{roleName}</span>}
        </div>
        {!compact&&<div style={{fontSize:11,color:C.hint,marginTop:2}}>{u.EMAIL}</div>}
        {franchises.length>0&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:3,marginTop:3}}>
            {franchises.map(f=>(
              <span key={f.SUBPROJECTID}
                style={{fontSize:9,fontWeight:600,background:accentLight,color:primary,
                  borderRadius:4,padding:"1px 5px"}}>
                {f.FRANCHISE}
              </span>
            ))}
          </div>
        )}
      </div>

      {!compact&&(
        <div style={{display:"flex",gap:12,flexShrink:0,fontSize:11,color:C.hint,alignItems:"center"}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontWeight:600,color:C.text,fontSize:12}}>{u.TOTAL_LOGIN||0}</div>
            <div>Logins</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontWeight:600,color:C.text,fontSize:12}}>{u.UPLOADS||0}</div>
            <div>Uploads</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontWeight:600,color:C.text,fontSize:12}}>{u.TOTALVIEWS||0}</div>
            <div>Views</div>
          </div>
          <div style={{textAlign:"center",minWidth:55}}>
            <div style={{fontWeight:600,color:C.text,fontSize:12}}>{u.LATEST_LOGIN||"—"}</div>
            <div>Last login</div>
          </div>
          <div style={{textAlign:"center",minWidth:60}}>
            <div style={{fontWeight:600,fontSize:12,
              color:status==="Expired"?"#D97706":status==="Inactive"?"#DC2626":C.text}}>
              {u.EXPIRYDATE||"—"}
            </div>
            <div>Expires</div>
          </div>
        </div>
      )}

      {/* 3-dot menu button — left side, vertical */}
      <div ref={menuRef} style={{position:"relative",flexShrink:0,marginRight:8,order:-1}}
        onClick={e=>e.stopPropagation()}>
        <button onClick={e=>{e.stopPropagation();setMenuOpen(o=>!o);}}
          style={{width:24,height:32,borderRadius:6,border:"none",
            background:menuOpen?"#F0F0F0":"transparent",cursor:"pointer",
            display:"flex",flexDirection:"column",alignItems:"center",
            justifyContent:"center",gap:3,padding:"4px 6px"}}>
          {[0,1,2].map(i=>(
            <div key={i} style={{width:4,height:4,borderRadius:"50%",
              background:menuOpen?"#374151":"#9CA3AF",flexShrink:0}}/>
          ))}
        </button>
        {menuOpen&&(
          <div style={{position:"absolute",left:0,top:"calc(100% + 4px)",
            background:"#fff",borderRadius:10,border:`1px solid ${C.border}`,
            boxShadow:"0 4px 16px rgba(0,0,0,.12)",zIndex:100,
            minWidth:160,overflow:"hidden"}}>
            {!isDeleted&&(
              <button onClick={()=>doAction(isActive?2:1)}
                style={{width:"100%",padding:"9px 14px",border:"none",background:"none",
                  textAlign:"left",cursor:"pointer",fontSize:13,
                  color:isActive?"#D97706":"#059669",fontWeight:600,
                  display:"flex",alignItems:"center",gap:8}}
                onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                onMouseLeave={e=>e.currentTarget.style.background="none"}>
                {isActive?"⏸ Deactivate":"▶ Activate"}
              </button>
            )}
            <button onClick={()=>doAction(3)}
              style={{width:"100%",padding:"9px 14px",border:"none",background:"none",
                textAlign:"left",cursor:"pointer",fontSize:13,
                color:"#EF4444",fontWeight:600,
                display:"flex",alignItems:"center",gap:8}}
              onMouseEnter={e=>e.currentTarget.style.background="#FFF5F5"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              🗑 Delete
            </button>
          </div>
        )}
      </div>

      {/* Toast — fixed bottom center */}
      {(actLoad||actMsg)&&ReactDOM.createPortal(
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
          zIndex:9999,padding:"10px 20px",borderRadius:10,
          fontSize:13,fontWeight:600,whiteSpace:"nowrap",
          boxShadow:"0 4px 16px rgba(0,0,0,.18)",
          background:actLoad?"#374151":actMsg.toLowerCase().includes("success")?"#059669":"#EF4444",
          color:"#fff",display:"flex",alignItems:"center",gap:8,
          animation:"fadeInUp .2s ease"}}>
          {actLoad
            ? <><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,.4)",
                borderTopColor:"#fff",borderRadius:"50%",
                animation:"spin .7s linear infinite",display:"inline-block"}}/>Processing…</>
            : actMsg}
          <style>{`
            @keyframes fadeInUp{from{opacity:0;transform:translate(-50%,8px)}to{opacity:1;transform:translate(-50%,0)}}
            @keyframes spin{to{transform:rotate(360deg)}}
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function UserManagement({ user, activeSub, onNavigate }) {
  const { theme }   = useContext(ThemeCtx)||{};
  const primary     = theme?.primary     || C.primary;
  const accentLight = theme?.accentLight || C.light;

  const [users,      setUsers]      = useState([]);
  const [drops,      setDrops]      = useState([[],[],[]]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [view,       setView]       = useState("list");
  const [editUser,   setEditUser]   = useState(null);
  const [toast,      setToast]      = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const showToast = (t) => { setToast(t); setTimeout(()=>setToast(null),3500); };

  const mountedSub = useRef(activeSub?.SUBPROJECTID);
  useEffect(()=>{
    // only navigate away if subproject actually changed or ADMINROLE lost
    if(activeSub?.SUBPROJECTID !== mountedSub.current) {
      onNavigate?.("ataglance");
    } else if(activeSub && !activeSub.ADMINROLE) {
      onNavigate?.("ataglance");
    }
  },[activeSub?.SUBPROJECTID, activeSub?.ADMINROLE]);

  useEffect(()=>{
    apiFetch("add_user_drop",{},user)
      .then(r=>{ if(r.STATUS==="1"&&Array.isArray(r.DATA)) setDrops(r.DATA); })
      .catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!activeSub) return;
    setLoading(true);
    apiFetch("view_user",{subprojectid:activeSub.SUBPROJECTID},user)
      .then(r=>{ if(r.STATUS==="1"&&Array.isArray(r.DATA)) setUsers(r.DATA); else setUsers([]); })
      .catch(()=>setUsers([]))
      .finally(()=>setLoading(false));
  },[activeSub?.SUBPROJECTID,refreshKey]);

  const filtered = users.filter(u=>
    (u.NAME||"").toLowerCase().includes(search.toLowerCase())||
    (u.EMAIL||"").toLowerCase().includes(search.toLowerCase())
  );

  const openAdd  = () => { setEditUser(null); setView("form"); };
  const openEdit = (u) => { setEditUser(u);   setView("form"); };
  const closeForm= () => { setEditUser(null); setView("list"); };

  return(
    <div style={{display:"flex",height:"100%",overflow:"hidden",background:"#F4F7FB"}}>

      {/* list panel */}
      <div style={{width:view==="list"?"100%":"340px",flexShrink:0,
        display:"flex",flexDirection:"column",
        borderRight:view==="form"?`1px solid ${C.border}`:"none",
        background:"#fff",overflow:"hidden",transition:"width 0.15s"}}>

        <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,
          display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:700,color:C.text}}>👥 Account</div>
            <div style={{fontSize:11,color:C.hint}}>{activeSub?.SUMMARY} · {users.length} users</div>
          </div>
          <div style={{position:"relative"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search…"
              style={{padding:"5px 8px 5px 26px",borderRadius:7,border:`1px solid ${C.border}`,
                fontSize:12,outline:"none",fontFamily:"inherit",color:C.text,
                background:"#F7F9FC",width:view==="form"?110:180}}/>
            <svg style={{position:"absolute",left:7,top:"50%",transform:"translateY(-50%)",
              pointerEvents:"none"}} width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke={C.hint} strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
          </div>
          <button onClick={openAdd}
            style={{padding:"6px 14px",borderRadius:7,border:"none",
              background:primary,color:"#fff",fontSize:12,fontWeight:600,
              cursor:"pointer",flexShrink:0,boxShadow:`0 2px 8px ${primary}44`}}>
            + Add
          </button>
        </div>

        <div style={{flex:1,overflowY:"auto"}}>
          {loading&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",
              paddingTop:60,gap:10,color:C.hint,flexDirection:"column"}}>
              <div style={{width:24,height:24,border:`3px solid ${C.border}`,
                borderTop:`3px solid ${primary}`,borderRadius:"50%",
                animation:"umSpin 0.7s linear infinite"}}/>
              <span style={{fontSize:12}}>Loading…</span>
            </div>
          )}
          {!loading&&filtered.length===0&&(
            <div style={{textAlign:"center",paddingTop:60,color:C.hint,fontSize:13}}>
              No users found
            </div>
          )}
          {!loading&&filtered.map(u=>(
            <UserRow key={u.USERID} u={u} primary={primary} accentLight={accentLight}
              compact={view==="form"} onEdit={openEdit}
              adminUser={user} onRefresh={()=>setRefreshKey(k=>k+1)}/>
          ))}
        </div>
      </div>

      {/* form panel */}
      {view==="form"&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"#fff"}}>
          <UserForm
            key={editUser?.USERID||"new"}
            user={user} activeSub={activeSub} drops={drops}
            editUser={editUser}
            onClose={closeForm}
            onSaved={()=>{ setRefreshKey(k=>k+1); closeForm(); }}
            onToast={showToast}
            primary={primary} accentLight={accentLight} theme={theme}/>
        </div>
      )}

      <Toast toast={toast}/>
      <style>{`
        @keyframes umSpin  { to{transform:rotate(360deg)} }
        @keyframes umToast { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}